import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { requestJSON, type AuthSession } from "../api";
import { useI18n } from "../i18n";
import {
  ManagementDialog,
  ManagementDrawer,
  ManagementMessages,
  ManagementPanel,
} from "../managementUI";
import { Empty, Table } from "./ui";
import { NotificationEditor } from "./NotificationEditor";

export type NotificationChannel = {
  id: string;
  name: string;
  kind: string;
  enabled: boolean;
  revision: number;
  matches: Record<string, string>;
  send_resolved: boolean;
  destination: string;
};
type Delivery = {
  id: string;
  channel_id: string;
  event_id: string;
  event_status: string;
  state: string;
  attempts: number;
  result_code: string;
  http_status: number;
  created_at_ms: number;
};
type Audit = {
  id: string;
  channel_id: string;
  actor: string;
  action: string;
  reason: string;
  created_at_ms: number;
};
const base = "/api/v1/monitoring/notification-";

export function NotificationChannels({ session }: { session: AuthSession }) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const drawer = params.get("notification_drawer");
  const channelFilter = params.get("notification_channel") || "";
  const closeDrawer = () => {
    const next = new URLSearchParams(params);
    next.delete("notification_drawer");
    next.delete("notification_channel");
    setParams(next);
  };
  const openDrawer = (kind: string, channelID = "") => {
    const next = new URLSearchParams(params);
    next.set("notification_drawer", kind);
    if (channelID) next.set("notification_channel", channelID);
    else next.delete("notification_channel");
    setOffset(0);
    setParams(next);
  };
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<
    NotificationChannel | null | undefined
  >();
  const [action, setAction] = useState<{
    kind: "test" | "delete" | "toggle";
    channel: NotificationChannel;
  } | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const [c, d, a] = await Promise.all([
          requestJSON<{ items: NotificationChannel[] }>(base + "channels", {
            signal,
          }),
          drawer === "deliveries"
            ? requestJSON<{ items: Delivery[] }>(
                base +
                  `deliveries?offset=${offset}&channel_id=${encodeURIComponent(channelFilter)}`,
                { signal },
              )
            : Promise.resolve({ items: [] as Delivery[] }),
          drawer === "audit"
            ? requestJSON<{ items: Audit[] }>(base + "audit", { signal })
            : Promise.resolve({ items: [] as Audit[] }),
        ]);
        if (!signal?.aborted) {
          setChannels(c.items);
          setDeliveries(d.items);
          setAudits(a.items);
          setError("");
        }
      } catch (e) {
        if (!signal?.aborted)
          setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [offset, drawer, channelFilter],
  );
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setDeliveries([]);
    setAudits([]);
    void refresh(controller.signal);
    const timer = setInterval(() => {
      if (document.visibilityState === "visible")
        void refresh(controller.signal);
    }, 10000);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [refresh]);
  async function confirm() {
    if (!action) return;
    setBusy(true);
    setActionError("");
    try {
      const c = action.channel;
      const path = base + "channels/" + c.id;
      await requestJSON(path + (action.kind === "test" ? "/test" : ""), {
        method:
          action.kind === "test"
            ? "POST"
            : action.kind === "delete"
              ? "DELETE"
              : "PUT",
        csrfToken: session.csrf_token,
        reason,
        body:
          action.kind === "test"
            ? { expected_revision: c.revision }
            : action.kind === "delete"
              ? { expected_revision: c.revision }
              : {
                  name: c.name,
                  enabled: !c.enabled,
                  expected_revision: c.revision,
                  matches: c.matches,
                  send_resolved: c.send_resolved,
                },
      });
      setAction(null);
      if (action.kind === "test") openDrawer("deliveries", c.id);
      setNotice(
        action.kind === "test"
          ? t(
              "测试已入队，请在投递记录中查看结果。",
              "Test queued. Check delivery records for the result.",
            )
          : t("通知渠道已更新。", "Notification channel updated."),
      );
      if (action.kind !== "test") await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  const state = (value: string) =>
    ({
      pending: t("待发送", "Pending"),
      sending: t("发送中", "Sending"),
      retry_wait: t("等待重试", "Retry pending"),
      accepted: t("接收方已接受", "Accepted by receiver"),
      failed: t("失败", "Failed"),
      cancelled: t("已取消", "Cancelled"),
      expired: t("已过期", "Expired"),
    })[value] || value;
  return (
    <div className="notification-view">
      <ManagementMessages error={action ? undefined : error} notice={notice} />
      <ManagementPanel
        title={t("通知渠道", "Notification channels")}
        actions={
          <div className="notification-tools">
            <button
              className="secondary-button"
              onClick={() => openDrawer("deliveries")}
            >
              {t("全部投递记录", "All deliveries")}
            </button>
            <button
              className="secondary-button"
              onClick={() => openDrawer("audit")}
            >
              {t("通知操作记录", "Notification operations")}
            </button>
            <button
              className="primary-button notification-add"
              onClick={() => setEditor(null)}
            >
              {t("添加渠道", "Add channel")}
            </button>
          </div>
        }
      >
        {loading ? (
          <Empty>{t("正在加载…", "Loading…")}</Empty>
        ) : !channels.length ? (
          <Empty>
            {t(
              "尚无通知渠道。添加并测试后，再启用自动告警通知。",
              "No channels. Add and test one before enabling automatic notifications.",
            )}
          </Empty>
        ) : (
          <Table
            headers={[
              t("名称 / 类型", "Name / type"),
              t("目标", "Destination"),
              t("状态", "State"),
              t("匹配条件", "Matches"),
              t("操作", "Actions"),
            ]}
          >
            {channels.map((c) => (
              <tr key={c.id}>
                <td>
                  <strong>{c.name}</strong>
                  <span>
                    {c.kind === "email" ? t("邮件", "Email") : "Webhook"}
                  </span>
                </td>
                <td>{c.destination}</td>
                <td>
                  {c.enabled ? t("已启用", "Enabled") : t("未启用", "Disabled")}
                </td>
                <td>
                  {Object.entries(c.matches)
                    .map(([k, v]) => `${k}=${v}`)
                    .join(", ") || t("当前DC全部告警", "All alerts in this DC")}
                  <span>
                    {c.send_resolved
                      ? t("包含恢复通知", "Includes resolved alerts")
                      : t("仅告警触发", "Firing only")}
                  </span>
                </td>
                <td>
                  <div className="table-actions">
                    <button
                      className="secondary-button"
                      onClick={() => openDrawer("deliveries", c.id)}
                    >
                      {t("投递记录", "Deliveries")}
                    </button>
                    <button
                      className="secondary-button"
                      onClick={() => setEditor(c)}
                    >
                      {t("编辑", "Edit")}
                    </button>
                    {(["toggle", "test", "delete"] as const).map((kind) => (
                      <button
                        key={kind}
                        className={
                          kind === "delete"
                            ? "danger-button"
                            : "secondary-button"
                        }
                        onClick={() => {
                          setAction({ kind, channel: c });
                          setReason("");
                          setError("");
                          setActionError("");
                        }}
                      >
                        {kind === "test"
                          ? t("测试", "Test")
                          : kind === "delete"
                            ? t("删除", "Delete")
                            : c.enabled
                              ? t("停用", "Disable")
                              : t("启用", "Enable")}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </ManagementPanel>
      {drawer === "deliveries" ? (
        <ManagementDrawer
          title={
            channelFilter
              ? `${channels.find((c) => c.id === channelFilter)?.name || channelFilter} · ${t("投递记录", "Delivery records")}`
              : t("全部投递记录", "All deliveries")
          }
          onClose={closeDrawer}
        >
          <ManagementMessages error={error} />
          <button className="secondary-button" onClick={() => void refresh()}>
            {t("刷新", "Refresh")}
          </button>
          {loading ? (
            <Empty>{t("正在读取投递记录…", "Loading deliveries…")}</Empty>
          ) : !deliveries.length ? (
            <Empty>
              {t(
                "暂无投递记录。告警触发不等于通知已送达。",
                "No delivery records. A firing alert does not prove delivery.",
              )}
            </Empty>
          ) : (
            <Table
              headers={[
                t("时间", "Time"),
                t("渠道", "Channel"),
                t("事件", "Event"),
                t("状态", "State"),
                t("尝试", "Attempts"),
                t("结果", "Result"),
              ]}
            >
              {deliveries.map((d) => (
                <tr key={d.id}>
                  <td>{new Date(d.created_at_ms).toLocaleString()}</td>
                  <td title={d.channel_id}>
                    {channels.find((c) => c.id === d.channel_id)?.name ||
                      d.channel_id.slice(0, 12) + "…"}
                  </td>
                  <td>
                    {d.event_status === "test"
                      ? t("测试", "Test")
                      : d.event_status === "resolved"
                        ? t("恢复", "Resolved")
                        : t("触发", "Firing")}
                    <span title={d.event_id}>{d.event_id.slice(0, 12)}…</span>
                  </td>
                  <td>{state(d.state)}</td>
                  <td>{d.attempts}</td>
                  <td>
                    {d.result_code === "WEBHOOK_HTTPS_REQUIRED"
                      ? t("需要 HTTPS 地址", "HTTPS is required")
                      : d.result_code === "WEBHOOK_DESTINATION_BLOCKED"
                        ? t("目标地址未获许可", "Destination not allowed")
                        : d.result_code || "—"}
                    {d.result_code.startsWith("WEBHOOK_") ? (
                      <span>{d.result_code}</span>
                    ) : null}
                    {d.http_status > 0 ? ` / HTTP ${d.http_status}` : ""}
                  </td>
                </tr>
              ))}
            </Table>
          )}
          <div className="directory-pagination">
            <button
              className="secondary-button"
              disabled={!offset}
              onClick={() => setOffset(Math.max(0, offset - 50))}
            >
              {t("上一页", "Previous")}
            </button>
            <span>{offset / 50 + 1}</span>
            <button
              className="secondary-button"
              disabled={deliveries.length < 50 || offset >= 100000}
              onClick={() => setOffset(offset + 50)}
            >
              {t("下一页", "Next")}
            </button>
          </div>
        </ManagementDrawer>
      ) : null}
      {drawer === "audit" ? (
        <ManagementDrawer
          title={t("通知操作记录", "Notification operations")}
          onClose={closeDrawer}
        >
          <ManagementMessages error={error} />
          {loading ? (
            <Empty>{t("正在读取操作记录…", "Loading operations…")}</Empty>
          ) : !audits.length ? (
            <Empty>{t("暂无操作记录。", "No operations yet.")}</Empty>
          ) : (
            <Table
              headers={[
                t("时间", "Time"),
                t("操作", "Action"),
                t("管理员", "Administrator"),
                t("原因", "Reason"),
              ]}
            >
              {audits.map((a) => (
                <tr key={a.id}>
                  <td>{new Date(a.created_at_ms).toLocaleString()}</td>
                  <td>
                    {a.action === "save"
                      ? t("保存", "Save")
                      : a.action === "test"
                        ? t("测试", "Test")
                        : a.action === "delete"
                          ? t("删除", "Delete")
                          : a.action}
                  </td>
                  <td title={a.actor}>
                    {a.actor === session.user.admin_user_id
                      ? session.user.display_name
                      : a.actor.slice(0, 16) + "…"}
                  </td>
                  <td>{a.reason}</td>
                </tr>
              ))}
            </Table>
          )}
        </ManagementDrawer>
      ) : null}
      {editor !== undefined ? (
        <NotificationEditor
          channel={editor}
          session={session}
          onClose={() => setEditor(undefined)}
          onSaved={() => {
            setEditor(undefined);
            void refresh();
          }}
        />
      ) : null}
      {action ? (
        <ManagementDialog
          title={
            action.kind === "test"
              ? t("发送测试通知", "Send test notification")
              : action.kind === "delete"
                ? t("删除通知渠道", "Delete notification channel")
                : action.channel.enabled
                  ? t("停用通知渠道", "Disable notification channel")
                  : t("启用通知渠道", "Enable notification channel")
          }
          disabled={busy}
          onClose={() => setAction(null)}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void confirm();
            }}
            className="configuration-form-body"
          >
            <p>
              {action.channel.name} — {action.channel.destination}
            </p>
            <p>
              {action.kind === "test"
                ? t(
                    "将实际发送一条测试消息，即使渠道未启用。",
                    "This sends a real test message, even for a disabled channel.",
                  )
                : action.kind === "delete" || action.channel.enabled
                  ? t(
                      "未发送任务会取消；已经开始的发送可能完成，历史记录保留。",
                      "Pending deliveries will be cancelled; an in-flight send may finish. History is retained.",
                    )
                  : t(
                      "匹配的告警将自动发送到此渠道。",
                      "Matching alerts will be sent automatically.",
                    )}
            </p>
            <ManagementMessages error={actionError} />
            <label>
              {t("操作原因", "Reason")}
              <input
                required
                maxLength={512}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
              />
            </label>
            <button
              className="primary-button"
              disabled={busy || !reason.trim()}
            >
              {t("确认", "Confirm")}
            </button>
          </form>
        </ManagementDialog>
      ) : null}
    </div>
  );
}
